import {
  SwipeableDrawer,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import React, { useMemo, useState, useRef, useEffect } from "react";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { lang } from "src/language/Strings";
import { InspectionResponse } from "src/types/InspectionUpdateRequest";

export default function AutoCompleteTextField({
  name,
  field,
  label,
  customOptions,
  required,
  error,
  defaultValue
}: {
  name: string;
  field?: keyof InspectionResponse;
  label: string;
  required?: boolean;
  customOptions?: string[];
  error?: string | React.ReactNode;
  defaultValue?: string;
}) {
  const { data: inspections } = useProjectVariations();

  const [value, setValue] = useState<string>(defaultValue || "");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>("");
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Focus the search field when the drawer opens
  useEffect(() => {
    if (open && searchFieldRef.current) {
      searchFieldRef.current.focus();
    }
  }, [open]);

  // Handle error prop changes
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setShowError(true);
      
      // Clear existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      // Auto-hide error after 5 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
    
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [error]);

  // Listen to validation events on the hidden input
  useEffect(() => {
    const hiddenInput = hiddenInputRef.current;
    if (!hiddenInput) return;

    const handleInvalid = (e: Event) => {
      e.preventDefault();
      const validationMessage = hiddenInput.validationMessage;
      if (validationMessage) {
        setErrorMessage(validationMessage);
        setShowError(true);
        
        // Clear existing timeout
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }
        
        // Auto-hide error after 5 seconds
        errorTimeoutRef.current = setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    };

    const handleInput = () => {
      // Clear error when user starts typing/selecting
      if (showError && value) {
        setShowError(false);
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }
      }
    };

    hiddenInput.addEventListener('invalid', handleInvalid);
    hiddenInput.addEventListener('input', handleInput);

    return () => {
      hiddenInput.removeEventListener('invalid', handleInvalid);
      hiddenInput.removeEventListener('input', handleInput);
    };
  }, [showError, value]);

  const options = useMemo(() => {
    if (customOptions && !field) return customOptions;
    if (!inspections) return [];
    const unique = new Set<string>();
    inspections.forEach((inspection) => {
      const val = inspection[field!];
      if (typeof val === "string" && val.trim()) {
        unique.add(val);
      }
    });
    return Array.from(unique).sort();
  }, [inspections, field]);

  const filtered = useMemo(() => {
    return options.filter((o) =>
     o &&
      o.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const handleSelect = (val: string) => {
    setValue(val);
    setSearch("");
    setOpen(false);
    // Clear error when a value is selected
    if (showError) {
      setShowError(false);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Custom label above input, not inside border */}
      <Box mb={0.5}>
        <InputLabel htmlFor={`autocomplete-${name}`} required={required}>
          {label}
        </InputLabel>
      </Box>
      <TextField
        value={value}
        id={`autocomplete-${name}`}
        fullWidth
        error={showError}
        onClick={() => setOpen(true)}
        InputProps={{ readOnly: true }}
      />
      {/* visually hidden input for form submission and validation */}
      <input 
        ref={hiddenInputRef}
        type="text" 
        readOnly
        name={name} 
        value={value} 
        required={required}
        style={{ 
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none'
        }}
        tabIndex={-1}
        aria-hidden="true"
      />
      {showError && errorMessage && (
        <FormHelperText error sx={{ mt: 0.5, mb: 0 }}>
          {errorMessage}
        </FormHelperText>
      )}
      {/* bottom sheet */}
      {open && <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            height: "100vh",
            borderRadius: "16px 16px 0 0",
          },
        }}
      >
        <Box p={2}>
        <InputLabel htmlFor={`autocomplete-${name}`} required={required}>
          {label}
        </InputLabel>
          <TextField
            fullWidth
            placeholder={lang.searchOrTypeNew}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputRef={searchFieldRef}
          />
        </Box>
        <List sx={{ overflow: "auto" }}>
          {filtered.map((option) => (
            <ListItemButton
              key={option}
              onClick={() => handleSelect(option)}
            >
              <ListItemText primary={option} />
            </ListItemButton>
          ))}
          {/* add new option */}
          {search.trim() !== "" && !options.includes(search.trim()) && (
            <ListItemButton onClick={() => handleSelect(search.trim())}>
              <ListItemText primary={`Add "${search.trim()}"`} />
            </ListItemButton>
          )}  
        </List>
      </SwipeableDrawer>
}
    </Box>
  );
}